import { GetSecretValueCommand, SecretsManagerClient } from "@aws-sdk/client-secrets-manager";
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { requestOptions } from "./configure-http-agent";
import { tryParseJson } from "../try-parse-json";
import { get } from "../get";
import { OAuthMappingConfig } from "./oauth-mappings";

/*
 * The OAuth Client retrieves and maintains OAuth credentials for PS Services Portal to use.  It provides
 * a function which will return headers for a configured client, and if necessary get new
 * OAuth credentials before doing so.  The credential retrieval process should appear completely
 * transparent, and this simply returns a set of headers to send along with your request.
 *
 *
 * How to use this module, in a nutshell:
 *
 * const getOAuthHeaders = require("path/to/oauth-client.js");
 * const headers = await getOAuthHeaders("itsm");
 * const httpResult = await request({method: "GET", uri: "/path", headers, ...moreOptions});
 *
 *
 * For a detailed example, check out https://github.azc.ext.hp.com/MSSI/daas-portal/blob/fc93bbf64e89d087f1e2d627890cb0a91513b1df/server/data/customers.js#L23
 * The example method gets the entire list of customers from ITSM (considering pagination)
 * It is a bad example of how to do this, though.  There are many things I would change.
 *
 *
 * Implementations where Dynamo and ITSM could be interchanged (or used together) should conform
 * to the open-closed principle of SOLID.  I never got around to this, because I was simply trying
 * to prove connection viability to ITSM.
 */

interface TokenInfo {
  accessToken: string;
  refreshToken: string;
  scope: string;
  tokenType: string;
  expires: number;
  expiresAt: number;
  headers?: Record<string, string>;
  apiEndpoint: string;
  oauthEndpoint: string;
}

interface RawTokenInfo {
  access_token: string;
  refresh_token: string;
  scope: string;
  token_type: string;
  expires_in: number;
}

interface OAuthSecret {
  username?: string;
  password?: string;
  clientId?: string;
  clientSecret?: string;
  oauthEndpoint?: string;
  apiEndpoint?: string;
  headers?: Record<string, string>;
}

const secretsManager = new SecretsManagerClient({ region: "us-west-2" });

const promiseCache: Record<string, Promise<TokenInfo>> = {};
const secretsCache: Record<string, OAuthSecret> = {};
const tokensCache: Record<string, TokenInfo> = {};

const isTokenExpired = (secretId: string): boolean => {
  if (!tokensCache[secretId] || !tokensCache[secretId].expiresAt || !tokensCache[secretId].accessToken) {
    return true;
  }

  const now = new Date().getTime();

  return (now >= tokensCache[secretId].expiresAt);
};

const fetchSecrets = async (secretId: string, force = false) => {
  if (force || !secretsCache[secretId] || !secretsCache[secretId].clientId) {
    const { SecretString } = await secretsManager.send(new GetSecretValueCommand({
      SecretId: secretId,
      VersionStage: "AWSCURRENT",
    }));

    secretsCache[secretId] = tryParseJson<OAuthSecret>(SecretString, {}) as OAuthSecret;
  }

  if (!secretsCache[secretId] || !secretsCache[secretId].clientId) {
    throw new Error(`Could not connect to ${secretId}`);
  }

  return secretsCache[secretId];
};

const getTokenRequestOptions = (secretId: string, oauthMapping: OAuthMappingConfig[]): AxiosRequestConfig => {
  const secrets: OAuthSecret = secretsCache[secretId] || {};
  const { oauthEndpoint } = secrets;

  if (!oauthEndpoint) {
    throw new Error(`Client ${secretId} is not a valid client.`);
  }

  let auth: Record<string, string> = {};
  const form = new URLSearchParams();
  let useForm = false;
  let qs: Record<string, string> = {};

  for (const entry of oauthMapping) {
    const value = entry.lookup ? get(secrets, entry.lookup) as string : entry.value;

    if (value !== undefined && value !== null) {
      if (entry.form) {
        form.set(entry.form, `${value}`);
        useForm = true;
      }

      if (entry.query) {
        qs = { ...qs, [entry.query]: `${value}` };
      }

      if (entry.auth) {
        auth = { ...auth, [entry.auth]: `${value}` };
      }
    }
  }

  const axiosCredentials = (auth?.username && auth?.password)
    ? { username: auth.username, password: auth.password }
    : undefined;

  const headers: Record<string, string> = {};

  if (useForm) {
    headers["Content-Type"] = "application/x-www-form-urlencoded";
  }

  return {
    method: "post",
    url: oauthEndpoint,
    data: useForm ? form.toString() : {},
    auth: axiosCredentials,
    params: qs,
    headers,
    ...requestOptions,
  };
};

const fetchTokens = async (secretId: string, oauthMapping: OAuthMappingConfig[]): Promise<TokenInfo> => {
  let secret = await fetchSecrets(secretId);

  const now = new Date().getTime();
  let tries = 0;
  let response: AxiosResponse | undefined;
  let responseData: RawTokenInfo | undefined;

  while (!response && !responseData && tries < 2) {
    tries += 1;

    try {
      // load request options here, since catch block may result in changes
      const opts = getTokenRequestOptions(secretId, oauthMapping);
      response = await axios(opts);
      responseData = response?.data as RawTokenInfo;
    } catch (error: unknown) {
      response = undefined;
      const axiosError = error as AxiosError;

      if (axiosError?.code && /^40[13]$/.test(`${(error as AxiosError).code}`)) {
        // reload secrets cache - perhaps they changed
        secret = await fetchSecrets(secretId, true);
      }
    }
  }

  if (!responseData?.access_token || !responseData?.expires_in) {
    throw new Error(`Could not connect to ${secretId}`);
  }

  const expiresAt = now + (responseData.expires_in * 1000);

  tokensCache[secretId] = {
    accessToken: responseData.access_token,
    refreshToken: responseData.refresh_token,
    scope: responseData.scope,
    tokenType: responseData.token_type,
    expires: responseData.expires_in,
    expiresAt,
    headers: { ...secret.headers, Authorization: `Bearer ${responseData.access_token}` },
    apiEndpoint: secret.apiEndpoint || "",
    oauthEndpoint: secret.oauthEndpoint || "",
  };

  return tokensCache[secretId];
};

async function getTokensWrapper(secretId: string, oauthMapping: OAuthMappingConfig[]): Promise<TokenInfo> {
  if (promiseCache[secretId] !== undefined) {
    return promiseCache[secretId];
  }

  if (!isTokenExpired(secretId)) {
    return tokensCache[secretId];
  }

  promiseCache[secretId] = fetchTokens(secretId, oauthMapping);
  return promiseCache[secretId];
}

export interface OAuthRequestParams {
  uriBase: string;
  headers: Record<string, string>;
}

export default async function getOAuthRequestBase(secretId: string, oauthMapping: OAuthMappingConfig[]): Promise<OAuthRequestParams> {
  const { headers = {}, apiEndpoint } = await getTokensWrapper(secretId, oauthMapping);
  return { uriBase: apiEndpoint, headers };
}
